/**
 * PricingModelConfigForm.tsx — Shared config form for plan pricing models.
 *
 * Renders model-type-specific form fields (flat, per_seat, usage, tiered)
 * with contextual help text and catalog dropdown for usage models.
 *
 * Used by both PlanEditForm (inline editing) and PricingModelsSection (modal editing).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

export const MODEL_TYPE_LABELS: Record<string, string> = {
  flat: 'Flat Rate',
  per_seat: 'Per Seat',
  usage: 'Usage Based',
  tiered: 'Tiered',
};

export const MODEL_TYPE_COLORS: Record<string, string> = {
  flat: 'bg-blue-50 text-blue-900 border-blue-200',
  per_seat: 'bg-violet-50 text-violet-900 border-violet-200',
  usage: 'bg-cyan-50 text-cyan-900 border-cyan-200',
  tiered: 'bg-amber-50 text-amber-900 border-amber-200',
};

export const MODEL_TYPE_BADGE_COLORS: Record<string, string> = {
  flat: 'bg-blue-100 text-blue-800',
  per_seat: 'bg-violet-100 text-violet-800',
  tiered: 'bg-amber-100 text-amber-800',
  usage: 'bg-cyan-100 text-cyan-800',
};

interface Props {
  modelType: string;
  config: Record<string, unknown>;
  setConfig: (c: Record<string, unknown>) => void;
  /** If true, hide the explanatory header banner (for compact inline use) */
  compact?: boolean;
}

export function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
}

export function formatModelSummary(modelType: string, config: Record<string, unknown>): string {
  switch (modelType) {
    case 'flat': return `${formatMoney(Number(config.amount_cents ?? 0) / 100, String(config.currency ?? 'USD'))}/mo`;
    case 'per_seat': {
      const price = formatMoney(Number(config.per_seat_cents ?? 0) / 100, String(config.currency ?? 'USD'));
      const included = Number(config.included_seats ?? 0);
      return included > 0 ? `${price}/seat (${included} included)` : `${price}/seat`;
    }
    case 'usage': {
      const mode = String(config.billing_mode ?? 'credits');
      const allowance = Number(config.credit_allowance_cents ?? 0);
      if (allowance > 0) return `${mode} · ${formatMoney(allowance / 100, 'USD')} allowance`;
      return `${mode} billing`;
    }
    case 'tiered': {
      const tiers = (config.tiers as Array<Record<string, unknown>>) ?? [];
      return `${tiers.length} tier${tiers.length !== 1 ? 's' : ''} configured`;
    }
    default: return '';
  }
}

// ---------------------------------------------------------------------------
// Tiered Pricing Editor — row-based UI for managing volume tiers
// ---------------------------------------------------------------------------

type Tier = { min_qty: number; max_qty: number | null; unit_price_cents: number };

function TieredEditor({ tiers, currency, onChange }: {
  tiers: Tier[];
  currency: string;
  onChange: (tiers: Tier[], currency: string) => void;
}): React.JSX.Element {
  function addTier(): void {
    const lastMax = tiers.length > 0 ? (tiers[tiers.length - 1].max_qty ?? 0) : 0;
    onChange([...tiers, { min_qty: lastMax + 1, max_qty: null, unit_price_cents: 0 }], currency);
  }

  function removeTier(idx: number): void {
    const next = tiers.filter((_, i) => i !== idx);
    // Re-chain: each tier's min_qty = previous tier's max_qty + 1
    for (let i = 1; i < next.length; i++) {
      next[i] = { ...next[i], min_qty: (next[i - 1].max_qty ?? 0) + 1 };
    }
    onChange(next, currency);
  }

  function updateTier(idx: number, patch: Partial<Tier>): void {
    const next = tiers.map((t, i) => i === idx ? { ...t, ...patch } : t);
    // Auto-chain: when max_qty changes, update the next tier's min_qty
    if (patch.max_qty !== undefined && idx < next.length - 1) {
      next[idx + 1] = { ...next[idx + 1], min_qty: (patch.max_qty ?? 0) + 1 };
    }
    onChange(next, currency);
  }

  const tierDollars = (cents: number) => (cents / 100).toFixed(2);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700">Pricing Tiers</label>
        <span className="text-xs text-slate-400">{tiers.length} tier{tiers.length !== 1 ? 's' : ''}</span>
      </div>

      {tiers.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 p-4 text-center">
          <p className="text-sm text-slate-500">No tiers defined</p>
          <p className="text-xs text-slate-400 mt-1">Add tiers to define volume-based price breakpoints.</p>
        </div>
      )}

      {tiers.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[1fr_1fr_1fr_36px] gap-px bg-slate-100 text-[11px] font-medium uppercase tracking-wider text-slate-500 px-3 py-2">
            <span>From (units)</span>
            <span>To (units)</span>
            <span>Price / unit</span>
            <span />
          </div>
          {/* Rows */}
          {tiers.map((tier, idx) => {
            const isLast = idx === tiers.length - 1;
            return (
              <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_36px] gap-2 items-center px-3 py-2 border-t border-slate-100 bg-white">
                {/* Min qty */}
                <input
                  type="number"
                  min="0"
                  value={tier.min_qty}
                  onChange={(e) => updateTier(idx, { min_qty: Number(e.target.value) })}
                  className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm tabular-nums bg-slate-50"
                  readOnly={idx > 0}
                  title={idx > 0 ? 'Auto-calculated from previous tier' : undefined}
                />
                {/* Max qty */}
                <div className="relative">
                  {isLast && tier.max_qty === null ? (
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-slate-500 italic">Unlimited</span>
                      <button
                        type="button"
                        onClick={() => updateTier(idx, { max_qty: tier.min_qty + 100 })}
                        className="text-[10px] text-cyan-600 hover:text-cyan-800"
                        title="Set a cap"
                      >cap</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={tier.min_qty}
                        value={tier.max_qty ?? ''}
                        onChange={(e) => updateTier(idx, { max_qty: e.target.value ? Number(e.target.value) : null })}
                        className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm tabular-nums"
                        placeholder="∞"
                      />
                      {isLast && (
                        <button
                          type="button"
                          onClick={() => updateTier(idx, { max_qty: null })}
                          className="text-[10px] text-slate-400 hover:text-slate-600 shrink-0"
                          title="Set to unlimited"
                        >∞</button>
                      )}
                    </div>
                  )}
                </div>
                {/* Unit price in dollars */}
                <div className="relative">
                  <span className="absolute left-2 top-1.5 text-xs text-slate-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tierDollars(tier.unit_price_cents)}
                    onChange={(e) => updateTier(idx, { unit_price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                    className="w-full rounded-md border border-slate-200 pl-5 pr-2 py-1.5 text-sm tabular-nums"
                    placeholder="0.00"
                  />
                </div>
                {/* Remove */}
                <button
                  type="button"
                  onClick={() => removeTier(idx)}
                  className="rounded-md p-1 text-red-400 hover:text-red-600 hover:bg-red-50 transition"
                  title="Remove tier"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add tier button */}
      <button
        type="button"
        onClick={addTier}
        className="w-full rounded-lg border-2 border-dashed border-slate-300 py-2 text-xs font-medium text-amber-700 hover:border-amber-400 hover:bg-amber-50/50 transition"
      >
        + Add Tier
      </button>

      {/* Currency */}
      <div>
        <label className="block text-sm font-medium text-slate-700">Currency</label>
        <select value={currency} onChange={(e) => onChange(tiers, e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="USD">USD — US Dollar</option>
          <option value="EUR">EUR — Euro</option>
          <option value="GBP">GBP — British Pound</option>
          <option value="AUD">AUD — Australian Dollar</option>
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function PricingModelConfigForm({ modelType, config, setConfig, compact }: Props): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();

  const { data: catalogs } = useQuery<{ id: string; name: string; description?: string }[]>({
    queryKey: ['usage-catalogs-slim'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalogs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!token && modelType === 'usage',
  });

  const dollars = (key: string) => (Number(config[key] ?? 0) / 100).toFixed(2);
  const setDollars = (key: string, val: string) => setConfig({ ...config, [key]: Math.round(parseFloat(val || '0') * 100), currency: String(config.currency ?? 'USD') });

  return (
    <div className="space-y-4">
      {/* FLAT RATE */}
      {modelType === 'flat' && (
        <>
          {!compact && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
              <p className="text-xs font-medium text-blue-800">Flat Rate Pricing</p>
              <p className="mt-0.5 text-xs text-blue-700">A fixed recurring charge billed every billing period regardless of usage.</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700">Monthly Amount</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-2 text-sm text-slate-400">$</span>
              <input type="number" step="0.01" min="0" value={dollars('amount_cents')} onChange={(e) => setDollars('amount_cents', e.target.value)} className="w-full rounded-lg border border-slate-300 pl-7 pr-3 py-2 text-sm" placeholder="0.00" />
            </div>
            <p className="mt-1 text-xs text-slate-500">Fixed amount charged each billing period.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Currency</label>
            <select value={String(config.currency ?? 'USD')} onChange={(e) => setConfig({ ...config, currency: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="USD">USD — US Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="AUD">AUD — Australian Dollar</option>
            </select>
          </div>
        </>
      )}

      {/* PER SEAT */}
      {modelType === 'per_seat' && (
        <>
          {!compact && (
            <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3">
              <p className="text-xs font-medium text-violet-800">Per Seat Pricing</p>
              <p className="mt-0.5 text-xs text-violet-700">Charges scale with the number of active users/seats in the org.</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700">Price per Seat / Month</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-2 text-sm text-slate-400">$</span>
              <input type="number" step="0.01" min="0" value={dollars('per_seat_cents')} onChange={(e) => setDollars('per_seat_cents', e.target.value)} className="w-full rounded-lg border border-slate-300 pl-7 pr-3 py-2 text-sm" placeholder="0.00" />
            </div>
            <p className="mt-1 text-xs text-slate-500">Billed for each seat beyond the included allowance.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Included Seats</label>
            <input type="number" min="0" value={Number(config.included_seats ?? 0)} onChange={(e) => setConfig({ ...config, included_seats: Number(e.target.value) })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="0" />
            <p className="mt-1 text-xs text-slate-500">Set to 0 to bill every seat from the first user.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Currency</label>
            <select value={String(config.currency ?? 'USD')} onChange={(e) => setConfig({ ...config, currency: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="USD">USD — US Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="AUD">AUD — Australian Dollar</option>
            </select>
          </div>
        </>
      )}

      {/* USAGE BASED */}
      {modelType === 'usage' && (
        <>
          {!compact && (
            <div className="rounded-lg border border-cyan-100 bg-cyan-50/50 p-3">
              <p className="text-xs font-medium text-cyan-800">Usage Based Pricing</p>
              <p className="mt-0.5 text-xs text-cyan-700">Charges based on actual consumption. Pricing is defined in a catalog that maps event types to costs.</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700">Billing Mode</label>
            <select value={String(config.billing_mode ?? 'credits')} onChange={(e) => setConfig({ ...config, billing_mode: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="credits">Credits — deducted in real-time from balance</option>
              <option value="arrears">Arrears — accumulated and invoiced at period end</option>
            </select>
            <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2.5">
              {config.billing_mode === 'arrears' ? (
                <p className="text-xs text-slate-600"><strong>Arrears:</strong> Usage tracked and invoiced at period end. Best for enterprise.</p>
              ) : (
                <p className="text-xs text-slate-600"><strong>Credits:</strong> Each event deducts from balance in real-time. Blocked when exhausted.</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Pricing Catalog</label>
            <select
              value={String(config.pricing_catalog_id ?? '')}
              onChange={(e) => setConfig({ ...config, pricing_catalog_id: e.target.value || undefined })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">— Select a pricing catalog —</option>
              {(catalogs ?? []).map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}{cat.description ? ` — ${cat.description}` : ''}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">Defines per-event-type pricing. Configure catalogs in <strong>Usage & Pricing</strong>.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Monthly Credit Allowance</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-2 text-sm text-slate-400">$</span>
              <input type="number" step="0.01" min="0" value={dollars('credit_allowance_cents')} onChange={(e) => setDollars('credit_allowance_cents', e.target.value)} className="w-full rounded-lg border border-slate-300 pl-7 pr-3 py-2 text-sm" placeholder="0.00" />
            </div>
            <p className="mt-1 text-xs text-slate-500">Auto-granted each period. $0 = pure pay-as-you-go.</p>
          </div>
        </>
      )}

      {/* TIERED */}
      {modelType === 'tiered' && (
        <>
          {!compact && (
            <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
              <p className="text-xs font-medium text-amber-800">Tiered Pricing</p>
              <p className="mt-0.5 text-xs text-amber-700">Volume-based pricing where per-unit cost decreases as consumption increases.</p>
            </div>
          )}
          <TieredEditor
            tiers={(config.tiers as Array<{ min_qty: number; max_qty: number | null; unit_price_cents: number }>) ?? []}
            currency={String(config.currency ?? 'USD')}
            onChange={(tiers, currency) => setConfig({ ...config, tiers, currency })}
          />
        </>
      )}
    </div>
  );
}
